// Faltantes y devoluciones de una guía ya confirmada (pedido de Rodrigo, 2026-10-20). La guía entra a
// primera hora para vender; al terminar la recepción física, lo que faltó o llegó dañado se anota aquí:
// sale del stock AL TIRO (sin aprobación de otra persona) y queda esperando la NC del proveedor. La
// factura se cruza con la guía y el pago retiene ese monto hasta que llegue la NC.
import { useState } from 'react';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { decimal } from '../comun/formato';
import { estimar } from './comun';
import { useAccionCompras, type LineaRecibida } from './datos';

export function DialogoFaltantes({
  empresaId,
  recepcionId,
  titulo,
  lineas,
  alCerrar,
  alListo,
}: {
  empresaId: string;
  recepcionId: string;
  titulo: string;
  lineas: LineaRecibida[];
  alCerrar: () => void;
  alListo: (numero: string) => void;
}) {
  const accion = useAccionCompras(empresaId);
  const [cantidades, setCantidades] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState('');
  const conAceptado = lineas.filter((l) => Number(l.cantidad_aceptada) > 0);
  const elegidas = conAceptado
    .map((l) => ({ l, c: estimar(cantidades[l.id] ?? '') }))
    .filter((x) => x.c > 0);
  const excede = elegidas.some((x) => x.c > Number(x.l.cantidad_aceptada));
  const valido = elegidas.length > 0 && !excede && motivo.trim().length >= 10;
  const grabar = () =>
    accion.mutate(
      {
        ruta: `/compras/recepciones/${recepcionId}/faltantes`,
        cuerpo: {
          motivo: motivo.trim(),
          lineas: elegidas.map((x) => ({
            recepcionLineaId: x.l.id,
            cantidad: String(Number(x.c.toFixed(4))),
          })),
        },
      },
      { onSuccess: (r) => alListo((r as { numeroVisible?: string }).numeroVisible ?? '') },
    );
  return (
    <Modal
      titulo={`Faltantes y devoluciones · ${titulo}`}
      alCerrar={alCerrar}
      ancho="ancho"
      pie={
        <>
          <button type="button" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            type="button"
            className="primario"
            disabled={!valido || accion.isPending}
            onClick={grabar}
          >
            {accion.isPending ? 'Grabando…' : 'Descontar del stock y esperar NC'}
          </button>
        </>
      }
    >
      <p className="ayuda">
        Lo que anotes aquí <b>sale del stock al tiro</b> y queda esperando la nota de crédito del
        proveedor. Cuando registres la factura, ese monto queda retenido del pago; la NC lo cierra.
      </p>
      <table className="grilla compacta" aria-label="Faltantes por producto">
        <thead>
          <tr>
            <th>Código</th>
            <th>Producto</th>
            <th>Unid.</th>
            <th className="der">Recibido</th>
            <th className="der">Faltó / dañado</th>
          </tr>
        </thead>
        <tbody>
          {conAceptado.map((l) => {
            const c = estimar(cantidades[l.id] ?? '');
            const mal = c > Number(l.cantidad_aceptada);
            return (
              <tr key={l.id}>
                <td className="mono">{l.sku}</td>
                <td>{l.descripcion}</td>
                <td>{l.presentacion_codigo}</td>
                <td className="der mono">{decimal(l.cantidad_aceptada)}</td>
                <td className="der">
                  <input
                    className={`celda-num ${mal ? 'con-error' : ''}`}
                    inputMode="decimal"
                    aria-label={`Faltante ${l.sku}`}
                    value={cantidades[l.id] ?? ''}
                    placeholder="0"
                    onChange={(e) =>
                      setCantidades((x) => ({ ...x, [l.id]: e.target.value.replace(',', '.') }))
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {excede && <p className="tono-malo">No puede faltar más de lo que se recibió.</p>}
      <label className="campo">
        <span className="etiqueta">Motivo (mínimo 10 letras)</span>
        <input
          aria-label="Motivo del faltante"
          placeholder="Ej.: faltaron 2 cajas en el camión; 1 caja llegó rota"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </label>
      {accion.isError && <PanelError error={accion.error} />}
    </Modal>
  );
}
